import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Upload, Plus, Search, Filter, Trash2, Edit2, 
  Gamepad2, Layers, GitCompare, Check, X, AlertCircle, AlertTriangle, ChevronDown,
  Sparkles, Loader2, ArrowUpDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = [
  { value: 'matching', label: 'Matching', icon: GitCompare, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  { value: 'word_search', label: 'Word Search', icon: Search, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  { value: 'flashcard', label: 'Flashcard', icon: Layers, color: 'text-blue-600', bgColor: 'bg-blue-100' }
];

const DIFFICULTY_OPTIONS = [
  { value: '', label: 'No Difficulty' },
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' }
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'topic_az', label: 'Topic A–Z' },
  { value: 'topic_za', label: 'Topic Z–A' },
  { value: 'subtopic_az', label: 'Subtopic A–Z' },
  { value: 'subtopic_za', label: 'Subtopic Z–A' },
];

// Combobox component for topic/subtopic selection with create option
function TopicCombobox({ value, onChange, options, placeholder, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [localInput, setLocalInput] = useState('');
  
  // Use value directly when not editing, local when editing
  const displayValue = isOpen ? localInput : (value || '');
  
  const handleFocus = () => {
    setLocalInput(value || '');
    setIsOpen(true);
  };
  
  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes((localInput || '').toLowerCase())
  );
  
  const showCreateOption = localInput && !options.some(opt => 
    opt.toLowerCase() === localInput.toLowerCase()
  );
  
  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={(e) => {
            setLocalInput(e.target.value);
            onChange(e.target.value);
          }}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg pr-8"
        />
        <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-slate-400" />
      </div>
      
      {isOpen && (filteredOptions.length > 0 || showCreateOption) && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredOptions.map(opt => (
            <button
              key={opt}
              type="button"
              onMouseDown={() => {
                onChange(opt);
                setLocalInput(opt);
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm"
            >
              {opt}
            </button>
          ))}
          {showCreateOption && (
            <button
              type="button"
              onMouseDown={() => {
                onChange(localInput);
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm text-blue-600 border-t"
            >
              + Create &quot;{localInput}&quot;
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminGames() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Shared topics (from quiz system)
  const [sharedTopics, setSharedTopics] = useState([]);
  const [sharedSubtopics, setSharedSubtopics] = useState({});
  
  // Multi-select filters (persisted via URL)
  const [selectedGameTypes, setSelectedGameTypes] = useState(() => {
    const g = searchParams.get('game_types');
    return g ? g.split(',') : [];
  });
  const [selectedTopics, setSelectedTopics] = useState(() => {
    const t = searchParams.get('topics');
    return t ? t.split(';;').filter(Boolean) : [];
  });
  const [selectedSubtopics, setSelectedSubtopics] = useState(() => {
    const s = searchParams.get('subtopics');
    return s ? s.split(';;').filter(Boolean) : [];
  });
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const st = searchParams.get('statuses');
    return st ? st.split(',') : [];
  });
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'newest');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page') || '1'),
    pageSize: 50,
    total: 0,
    totalPages: 0
  });
  
  // All unique topics from game questions (for filter chips)
  const [gameTopics, setGameTopics] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // question to delete
  const [showAIModal, setShowAIModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // AI form state
  const [aiFormData, setAiFormData] = useState({
    topic: '',
    subtopic: '',
    game_type: 'flashcard',
    difficulty: ''
  });
  
  // Form state - default to DRAFT
  const [formData, setFormData] = useState({
    topic: '',
    subtopic: '',
    game_type: 'matching',
    prompt: '',
    correct_answer: '',
    rationale: '',
    difficulty: '',
    status: 'draft'
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Persist filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedGameTypes.length) params.set('game_types', selectedGameTypes.join(','));
    if (selectedTopics.length) params.set('topics', selectedTopics.join(';;'));
    if (selectedSubtopics.length) params.set('subtopics', selectedSubtopics.join(';;'));
    if (selectedStatuses.length) params.set('statuses', selectedStatuses.join(','));
    if (sortBy !== 'newest') params.set('sort', sortBy);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (pagination.page > 1) params.set('page', pagination.page.toString());
    setSearchParams(params, { replace: true });
  }, [selectedGameTypes, selectedTopics, selectedSubtopics, selectedStatuses, sortBy, debouncedSearch, pagination.page, setSearchParams]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedGameTypes.length > 0) params.append('game_types', selectedGameTypes.join(','));
      if (selectedTopics.length > 0) params.append('topics', selectedTopics.join(';;'));
      if (selectedSubtopics.length > 0) params.append('subtopics', selectedSubtopics.join(';;'));
      if (selectedStatuses.length > 0) params.append('statuses', selectedStatuses.join(','));
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('sort', sortBy);
      params.append('page', pagination.page.toString());
      params.append('page_size', pagination.pageSize.toString());
      
      const res = await axios.get(`${BACKEND_URL}/api/admin/games/questions?${params}`, { withCredentials: true });
      setQuestions(res.data.questions || []);
      if (res.data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: res.data.pagination.total,
          totalPages: res.data.pagination.total_pages
        }));
      }
    } catch (err) {
      toast.error('Failed to load game questions');
    } finally {
      setLoading(false);
    }
  }, [selectedGameTypes, selectedTopics, selectedSubtopics, selectedStatuses, debouncedSearch, sortBy, pagination.page, pagination.pageSize]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/games/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
  };

  const fetchGameTopics = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/games/questions/topics`, { withCredentials: true });
      setGameTopics(res.data || []);
    } catch (err) {
      console.error('Failed to load game topics');
    }
  };

  // Fetch shared topics from quiz system
  const fetchSharedTopics = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/topics`, { withCredentials: true });
      const topics = res.data || [];
      setSharedTopics(topics.map(t => t.topic_display_name || t.topic_key));
      
      // Build subtopics map
      const subMap = {};
      for (const t of topics) {
        try {
          const subRes = await axios.get(`${BACKEND_URL}/api/admin/subtopics/${encodeURIComponent(t.topic_key)}`, { withCredentials: true });
          subMap[t.topic_display_name || t.topic_key] = (subRes.data || []).map(s => s.display || s.key);
        } catch (e) {
          subMap[t.topic_display_name || t.topic_key] = [];
        }
      }
      setSharedSubtopics(subMap);
    } catch (err) {
      console.error('Failed to load shared topics');
    }
  };

  useEffect(() => {
    Promise.all([fetchQuestions(), fetchStats(), fetchGameTopics(), fetchSharedTopics()])
      .finally(() => setLoading(false));
  }, [fetchQuestions]);

  const handleCreateOrUpdate = async () => {
    // Validation
    if (!formData.topic.trim()) {
      toast.error('Topic is required');
      return;
    }
    if (!formData.prompt.trim()) {
      toast.error('Prompt is required');
      return;
    }
    if (!formData.correct_answer.trim()) {
      toast.error('Correct answer is required');
      return;
    }
    if (!formData.rationale.trim()) {
      toast.error('Rationale is required');
      return;
    }
    
    // Word Search validation warning
    if (formData.game_type === 'word_search') {
      const cleanAnswer = formData.correct_answer.toUpperCase().replace(/[^A-Z]/g, '');
      if (cleanAnswer.length < 2) {
        toast.error('Word Search answer must have at least 2 letters');
        return;
      }
      if (cleanAnswer.length > 20) {
        toast.error('Word Search answer must be 20 letters or fewer');
        return;
      }
    }

    try {
      if (editingQuestion) {
        const res = await axios.put(
          `${BACKEND_URL}/api/admin/games/questions/${editingQuestion.question_id}`,
          formData,
          { withCredentials: true }
        );
        if (res.data.validation_errors) {
          toast.warning('Question updated with warnings - saved as draft');
        } else {
          toast.success('Question updated');
        }
      } else {
        const res = await axios.post(
          `${BACKEND_URL}/api/admin/games/questions`,
          formData,
          { withCredentials: true }
        );
        if (res.data.validation_errors) {
          toast.warning('Question created with warnings - saved as draft');
        } else {
          toast.success('Question created');
        }
      }
      
      setShowCreateModal(false);
      setEditingQuestion(null);
      resetForm();
      fetchQuestions();
      fetchStats();
      fetchGameTopics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save question');
    }
  };

  const handleDelete = async (questionId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/games/questions/${questionId}`, { withCredentials: true });
      toast.success('Question deleted');
      setDeleteConfirm(null);
      fetchQuestions();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  const handleGenerateAI = async () => {
    if (!aiFormData.topic.trim()) {
      toast.error('Topic is required');
      return;
    }
    if (!aiFormData.game_type) {
      toast.error('Game type is required');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/games/questions/generate-ai`,
        aiFormData,
        { withCredentials: true }
      );

      // Pre-fill the create form with generated data
      setFormData({
        topic: response.data.topic || aiFormData.topic,
        subtopic: response.data.subtopic || aiFormData.subtopic || '',
        game_type: response.data.game_type || aiFormData.game_type,
        prompt: response.data.prompt,
        correct_answer: response.data.correct_answer,
        rationale: response.data.rationale,
        difficulty: response.data.difficulty || aiFormData.difficulty || '',
        status: 'draft'
      });

      setShowAIModal(false);
      setShowCreateModal(true);
      setEditingQuestion(null);
      toast.success('Question generated! Review and save when ready.');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 503) {
        toast.error(detail || 'AI service temporarily unavailable.');
      } else {
        toast.error(detail || 'Failed to generate question. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEdit = (question) => {
    setFormData({
      topic: question.topic || '',
      subtopic: question.subtopic || '',
      game_type: question.game_type || 'matching',
      prompt: question.prompt || '',
      correct_answer: question.correct_answer || '',
      rationale: question.rationale || '',
      difficulty: question.difficulty || '',
      status: question.status || 'draft'
    });
    setEditingQuestion(question);
    setShowCreateModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/games/upload-csv`,
        formDataUpload,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setUploadResults(res.data);
      
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} game questions (as draft)`);
        fetchQuestions();
        fetchStats();
        fetchGameTopics();
      }
      
      if (res.data.skipped > 0) {
        toast.warning(`${res.data.skipped} rows skipped due to errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      topic: '',
      subtopic: '',
      game_type: 'matching',
      prompt: '',
      correct_answer: '',
      rationale: '',
      difficulty: '',
      status: 'draft'
    });
  };

  // Toggle functions for multi-select filters
  const toggleGameType = (gameType) => {
    setSelectedGameTypes(prev => 
      prev.includes(gameType) ? prev.filter(g => g !== gameType) : [...prev, gameType]
    );
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleTopic = (topic) => {
    setSelectedTopics(prev => {
      const next = prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic];
      // Clear subtopics if no topics selected
      if (next.length === 0) setSelectedSubtopics([]);
      return next;
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleSubtopic = (subtopic) => {
    setSelectedSubtopics(prev => 
      prev.includes(subtopic) ? prev.filter(s => s !== subtopic) : [...prev, subtopic]
    );
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSelectedGameTypes([]);
    setSelectedTopics([]);
    setSelectedSubtopics([]);
    setSelectedStatuses([]);
    setSearchQuery('');
    setSortBy('newest');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = selectedGameTypes.length > 0 || selectedTopics.length > 0 || selectedSubtopics.length > 0 || selectedStatuses.length > 0 || searchQuery;

  // Get available subtopics based on selected topics (for filter) - from gameTopics API data
  const availableFilterSubtopics = useMemo(() => {
    if (selectedTopics.length === 0) return [];
    const subtopicSet = new Set();
    gameTopics.forEach(topic => {
      if (selectedTopics.includes(topic.topic) && topic.subtopics) {
        topic.subtopics.forEach(sub => {
          // Filter out null, undefined, empty strings
          if (sub && typeof sub === 'string' && sub.trim()) {
            subtopicSet.add(sub);
          }
        });
      }
    });
    return Array.from(subtopicSet).sort();
  }, [selectedTopics, gameTopics]);

  // Get all unique topics from game questions (for filter chips)
  const allFilterTopics = useMemo(() => {
    const topicSet = new Set();
    questions.forEach(q => {
      if (q.topic) topicSet.add(q.topic);
    });
    // Also merge with shared topics
    sharedTopics.forEach(t => topicSet.add(t));
    return Array.from(topicSet).sort();
  }, [questions, sharedTopics]);

  const getGameTypeInfo = (type) => GAME_TYPES.find(g => g.value === type) || GAME_TYPES[0];
  
  // Get all unique topics for combobox (merge shared + game topics)
  const allTopicOptions = [...new Set([
    ...sharedTopics,
    ...gameTopics.map(t => t.topic)
  ])].sort();
  
  // Get subtopics for selected topic (for create/edit form)
  const subtopicOptions = formData.topic 
    ? [...new Set([
        ...(sharedSubtopics[formData.topic] || []),
        ...(gameTopics.find(t => t.topic === formData.topic)?.subtopics || [])
      ])].filter(Boolean).sort()
    : [];

  // Get subtopics for AI form topic
  const aiSubtopicOptions = aiFormData.topic 
    ? [...new Set([
        ...(sharedSubtopics[aiFormData.topic] || []),
        ...(gameTopics.find(t => t.topic === aiFormData.topic)?.subtopics || [])
      ])].filter(Boolean).sort()
    : [];

  if (loading && questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gamepad2 className="w-6 h-6 text-purple-600" />
              <h1 className="text-2xl font-bold text-slate-900">Game Questions</h1>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowUploadModal(true)}
                variant="outline"
                className="gap-2"
              >
                <Upload className="w-4 h-4" /> Upload CSV
              </Button>
              <Button
                onClick={() => { 
                  setAiFormData({ topic: '', subtopic: '', game_type: 'flashcard', difficulty: '' });
                  setShowAIModal(true); 
                }}
                variant="outline"
                className="gap-2 border-purple-300 text-purple-600 hover:bg-purple-50"
              >
                <Sparkles className="w-4 h-4" /> Generate AI
              </Button>
              <Button
                onClick={() => { resetForm(); setEditingQuestion(null); setShowCreateModal(true); }}
                className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4" /> Add Question
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">Total</div>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">Matching</div>
              <div className="text-2xl font-bold text-purple-600">{stats.by_type.matching || 0}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">Word Search</div>
              <div className="text-2xl font-bold text-emerald-600">{stats.by_type.word_search || 0}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">Flashcard</div>
              <div className="text-2xl font-bold text-blue-600">{stats.by_type.flashcard || 0}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">Draft</div>
              <div className="text-2xl font-bold text-amber-600">{stats.by_status.draft || 0}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">Published</div>
              <div className="text-2xl font-bold text-green-600">{stats.by_status.published || 0}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-slate-700">Filters</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 hover:text-slate-700">
                <X className="w-3 h-3 mr-1" /> Clear All
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-slate-400" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {/* Game Types Multi-select */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-2 block">Game Type</label>
              <div className="flex flex-wrap gap-1.5">
                {GAME_TYPES.map(g => {
                  const Icon = g.icon;
                  return (
                    <button
                      key={g.value}
                      onClick={() => toggleGameType(g.value)}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-all flex items-center gap-1.5 ${
                        selectedGameTypes.includes(g.value)
                          ? `${g.bgColor} ${g.color} border-current`
                          : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {g.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Topics Multi-select */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-2 block">Topics</label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {allFilterTopics.map(topic => (
                  <button
                    key={topic}
                    onClick={() => toggleTopic(topic)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                      selectedTopics.includes(topic)
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                    }`}
                  >
                    {topic}
                  </button>
                ))}
                {allFilterTopics.length === 0 && (
                  <p className="text-xs text-slate-400 italic">No topics yet</p>
                )}
              </div>
            </div>
            
            {/* Subtopics Multi-select */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-2 block">Subtopics</label>
              {selectedTopics.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Select a topic first</p>
              ) : availableFilterSubtopics.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No subtopics for selected topics</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {availableFilterSubtopics.map(subtopic => (
                    <button
                      key={subtopic}
                      onClick={() => toggleSubtopic(subtopic)}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                        selectedSubtopics.includes(subtopic)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                      }`}
                    >
                      {subtopic}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Status Multi-select */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-2 block">Status</label>
              <div className="flex flex-wrap gap-1.5">
                {['draft', 'published'].map(status => (
                  <button
                    key={status}
                    onClick={() => toggleStatus(status)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-all capitalize ${
                      selectedStatuses.includes(status)
                        ? status === 'published' ? 'bg-green-500 text-white border-green-500' : 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Search */}
          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search prompts and answers..."
                className="pl-10"
              />
            </div>
            <span className="text-sm text-slate-500">
              Showing {questions.length} of {pagination.total} questions
            </span>
          </div>
        </div>

        {/* Questions List */}
        {questions.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Gamepad2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Game Questions Yet</h3>
            <p className="text-slate-600 mb-4">Upload a CSV or create questions manually to get started.</p>
            <Button onClick={() => setShowUploadModal(true)} variant="outline">
              <Upload className="w-4 h-4 mr-2" /> Upload CSV
            </Button>
          </div>
        ) : (
          <>
          <div className="space-y-3">
            {questions.map((q) => {
              const gameInfo = getGameTypeInfo(q.game_type);
              const GameIcon = gameInfo.icon;
              const hasValidationErrors = q.validation_errors && q.validation_errors.length > 0;
              
              return (
                <div key={q.question_id} className={`bg-white rounded-xl border p-4 ${hasValidationErrors ? 'border-amber-300' : 'border-slate-200'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`p-2 bg-slate-100 rounded-lg ${gameInfo.color}`}>
                      <GameIcon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                          {q.topic}
                        </span>
                        {q.subtopic && (
                          <span className="text-xs px-2 py-0.5 bg-slate-50 text-slate-500 rounded">
                            {q.subtopic}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          q.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {q.status}
                        </span>
                        {q.difficulty && (
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                            {q.difficulty}
                          </span>
                        )}
                        {hasValidationErrors && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Invalid
                          </span>
                        )}
                      </div>
                      
                      <p className="text-slate-900 font-medium mb-1 line-clamp-2">{q.prompt}</p>
                      <p className="text-sm text-emerald-600">Answer: {q.correct_answer}</p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-1">{q.rationale}</p>
                      
                      {hasValidationErrors && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                          {q.validation_errors.join('; ')}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 shrink-0">
                      <Button
                        onClick={() => handleEdit(q)}
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-slate-700"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDeleteConfirm(q)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </Button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editingQuestion ? 'Edit Game Question' : 'Create Game Question'}
              </h2>
              <button onClick={() => { setShowCreateModal(false); setEditingQuestion(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Game Type *</label>
                  <select
                    value={formData.game_type}
                    onChange={(e) => setFormData({...formData, game_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    {GAME_TYPES.map(g => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">New questions default to Draft</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Topic * (select or create)</label>
                  <TopicCombobox
                    value={formData.topic}
                    onChange={(val) => setFormData({...formData, topic: val, subtopic: ''})}
                    options={allTopicOptions}
                    placeholder="e.g., Hemodynamics"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subtopic (select or create)</label>
                  <TopicCombobox
                    value={formData.subtopic}
                    onChange={(val) => setFormData({...formData, subtopic: val})}
                    options={subtopicOptions}
                    placeholder="e.g., Vasopressors"
                    disabled={!formData.topic}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {DIFFICULTY_OPTIONS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prompt *</label>
                <textarea
                  value={formData.prompt}
                  onChange={(e) => setFormData({...formData, prompt: e.target.value})}
                  placeholder="The clinical question or clue (answer should NOT appear here)"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Correct Answer *
                  {formData.game_type === 'word_search' && (
                    <span className="text-xs text-amber-600 ml-2">(Word Search: 2-20 letters only)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={formData.correct_answer}
                  onChange={(e) => setFormData({...formData, correct_answer: e.target.value})}
                  placeholder="The exact answer"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
                {formData.game_type === 'word_search' && formData.correct_answer && (
                  <p className="text-xs text-slate-500 mt-1">
                    Clean word: {formData.correct_answer.toUpperCase().replace(/[^A-Z]/g, '')} 
                    ({formData.correct_answer.toUpperCase().replace(/[^A-Z]/g, '').length} letters)
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rationale *</label>
                <textarea
                  value={formData.rationale}
                  onChange={(e) => setFormData({...formData, rationale: e.target.value})}
                  placeholder="1-2 sentences explaining why the answer is correct"
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingQuestion(null); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateOrUpdate} className="bg-purple-600 hover:bg-purple-700 text-white">
                {editingQuestion ? 'Update' : 'Create'} Question
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full">
            <div className="border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Upload Game Questions (CSV)</h2>
              <button onClick={() => { setShowUploadModal(false); setUploadResults(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  All uploaded questions will be saved as <strong>Draft</strong> by default. Review and publish them manually.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-slate-900 mb-2">Required CSV Columns:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><code className="bg-slate-200 px-1 rounded">topic</code> (required)</div>
                  <div><code className="bg-slate-200 px-1 rounded">subtopic</code> (optional)</div>
                  <div><code className="bg-slate-200 px-1 rounded">game_type</code> (required)</div>
                  <div><code className="bg-slate-200 px-1 rounded">prompt</code> (required)</div>
                  <div><code className="bg-slate-200 px-1 rounded">correct_answer</code> (required)</div>
                  <div><code className="bg-slate-200 px-1 rounded">rationale</code> (required)</div>
                  <div><code className="bg-slate-200 px-1 rounded">difficulty</code> (optional)</div>
                  <div><code className="bg-slate-200 px-1 rounded">status</code> (optional)</div>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  <strong>game_type</strong>: matching, word_search, or flashcard<br />
                  <strong>difficulty</strong>: easy, medium, or hard<br />
                  <strong>status</strong>: draft or published (defaults to draft)
                </p>
              </div>
              
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="csv-upload"
                  className={`cursor-pointer ${uploading ? 'opacity-50' : ''}`}
                >
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 mb-2">
                    {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                  </p>
                  <p className="text-xs text-slate-400">Game questions only</p>
                </label>
              </div>
              
              {uploadResults && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium text-slate-900 mb-3">Upload Results</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-slate-900">{uploadResults.total_rows}</div>
                      <div className="text-slate-500">Total Rows</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{uploadResults.imported}</div>
                      <div className="text-slate-500">Imported (Draft)</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-amber-600">{uploadResults.skipped}</div>
                      <div className="text-slate-500">Skipped</div>
                    </div>
                  </div>
                  
                  {uploadResults.by_game_type && (
                    <div className="flex gap-4 text-sm mb-4">
                      <span className="text-purple-600">Matching: {uploadResults.by_game_type.matching || 0}</span>
                      <span className="text-emerald-600">Word Search: {uploadResults.by_game_type.word_search || 0}</span>
                      <span className="text-blue-600">Flashcard: {uploadResults.by_game_type.flashcard || 0}</span>
                    </div>
                  )}
                  
                  {uploadResults.errors && uploadResults.errors.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> Errors ({uploadResults.errors.length})
                      </h4>
                      <div className="max-h-40 overflow-y-auto text-xs space-y-2">
                        {uploadResults.errors.slice(0, 10).map((err, idx) => (
                          <div key={idx} className="bg-red-50 p-2 rounded">
                            <strong>Row {err.row}:</strong> {err.errors.join(', ')}
                          </div>
                        ))}
                        {uploadResults.errors.length > 10 && (
                          <p className="text-slate-500">...and {uploadResults.errors.length - 10} more errors</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Done button inside results section for visibility */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <Button 
                      onClick={() => { setShowUploadModal(false); setUploadResults(null); }}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Check className="w-4 h-4 mr-2" /> Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="border-t border-slate-200 p-4 flex justify-end">
              <Button onClick={() => { setShowUploadModal(false); setUploadResults(null); }}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 text-center mb-2">
                Delete Game Question?
              </h3>
              <p className="text-sm text-slate-600 text-center mb-4">
                This action cannot be undone. The following question will be permanently deleted:
              </p>
              <div className="bg-slate-50 rounded-lg p-3 mb-6">
                <p className="text-sm text-slate-700 font-medium line-clamp-2 mb-1">{deleteConfirm.prompt}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded">
                    {deleteConfirm.game_type}
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded">
                    {deleteConfirm.topic}
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setDeleteConfirm(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(deleteConfirm.question_id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete Question
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold">Generate AI Game Question</h2>
              </div>
              <button onClick={() => setShowAIModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                <p>AI will generate a game question based on your inputs. The result will be saved as <strong>Draft</strong> for review.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Game Type *</label>
                <select
                  value={aiFormData.game_type}
                  onChange={(e) => setAiFormData({...aiFormData, game_type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {GAME_TYPES.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
                {aiFormData.game_type === 'word_search' && (
                  <p className="text-xs text-amber-600 mt-1">Word Search answers will be single words only (A-Z, no spaces)</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Topic * (select or create)</label>
                <TopicCombobox
                  value={aiFormData.topic}
                  onChange={(val) => setAiFormData({...aiFormData, topic: val})}
                  options={allTopicOptions}
                  placeholder="e.g., Hemodynamics"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtopic (optional)</label>
                <TopicCombobox
                  value={aiFormData.subtopic}
                  onChange={(val) => setAiFormData({...aiFormData, subtopic: val})}
                  options={aiSubtopicOptions}
                  placeholder="e.g., Vasopressors"
                  disabled={!aiFormData.topic}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty (optional)</label>
                <select
                  value={aiFormData.difficulty}
                  onChange={(e) => setAiFormData({...aiFormData, difficulty: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {DIFFICULTY_OPTIONS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="border-t border-slate-200 p-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAIModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleGenerateAI}
                disabled={isGenerating || !aiFormData.topic}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" /> Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
